import PropTypes from 'prop-types';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  Stack,
  Dialog,
  Button,
  Divider,
  Tooltip,
  Skeleton,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Zoom,
} from '@mui/material';
import api, { isSessionExpiredError } from '../../api';
import Iconify from '../iconify';
import { toast } from 'react-hot-toast';
import { canManagePackageActions, canViewPaymentDetails } from '../../utils/roleAccess';
import { formatHubScanStatus } from '../../utils/hubScanActions';

// ----------------------------------------------------------------------

const Transition = forwardRef((props, ref) => <Zoom ref={ref} {...props} />);

const formatPaymentType = (value) => (value === 'Without To Pay' ? 'Credit' : value);

const formatDateTime = (data) => {
  if (!data) return '—';
  const date = new Date(data);
  if (Number.isNaN(date.getTime())) return String(data);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const localTime = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return `${day}-${month}-${year} ${localTime}`;
};

const formatStatusLabel = (status) => formatHubScanStatus(status);

const statusTone = (status, theme) => {
  const key = String(status || '').toUpperCase();
  if (key === 'DELIVERED') {
    return {
      color: theme.palette.success.dark,
      bg: alpha(theme.palette.success.main, 0.12),
      icon: 'solar:check-circle-bold-duotone',
    };
  }
  if (key === 'BOOKED') {
    return {
      color: theme.palette.info.dark,
      bg: alpha(theme.palette.info.main, 0.12),
      icon: 'solar:box-bold-duotone',
    };
  }
  if (key === 'RECEIVED_AT_HUB' || key.includes('ARRIVED')) {
    return {
      color: theme.palette.info.dark,
      bg: alpha(theme.palette.info.main, 0.12),
      icon: 'mdi:warehouse',
    };
  }
  if (key === 'DISPATCHED_TO_HUB' || key === 'CANCEL_DISPATCH' || key.includes('DEPARTED')) {
    return {
      color: theme.palette.primary.dark,
      bg: alpha(theme.palette.primary.main, 0.12),
      icon: 'mdi:truck-fast',
    };
  }
  if (key.includes('OUT') || key.includes('DELIVERY')) {
    return {
      color: theme.palette.warning.dark,
      bg: alpha(theme.palette.warning.main, 0.14),
      icon: 'mdi:truck-delivery',
    };
  }
  return {
    color: theme.palette.primary.dark,
    bg: alpha(theme.palette.primary.main, 0.12),
    icon: 'solar:routing-2-bold-duotone',
  };
};

const isPackageEditable = (tracking, pkg) => {
  const current = String(tracking?.currentStatus || '').toUpperCase();
  if (current) {
    if (current === 'BOOKED') return true;
    // Hub movement and last-mile statuses lock editing.
    if (
      current === 'DISPATCHED_TO_HUB' ||
      current === 'RECEIVED_AT_HUB' ||
      current === 'CANCEL_DISPATCH' ||
      current.includes('OUT') ||
      current === 'DELIVERED' ||
      current.includes('FAILED') ||
      current === 'RETURNED' ||
      current === 'RESCHEDULED'
    ) {
      return false;
    }
    // Legacy free-text hub statuses
    if (current.includes('ARRIVED') || current.includes('DEPARTED')) return false;
    return current === 'BOOKED';
  }
  // Fallback when tracking payload is incomplete.
  return !pkg?.deliveredAt;
};

const MetaItem = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        mt: 0.4,
        fontWeight: 600,
        wordBreak: 'break-word',
        color: value ? 'text.primary' : 'text.disabled',
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

MetaItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

const SectionCard = ({ title, icon, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        height: '100%',
        border: `1px solid ${alpha(theme.palette.grey[500], 0.14)}`,
        background: `linear-gradient(
          160deg,
          ${alpha(theme.palette.background.paper, 0.98)} 0%,
          ${alpha(theme.palette.grey[500], 0.04)} 100%
        )`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.75 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          <Iconify icon={icon} width={16} />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
};

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  children: PropTypes.node,
};

// ----------------------------------------------------------------------

export default function PackageDetailModal({
  open,
  consignmentNo,
  onClose,
  showViewInList = true,
  fetchUrl = null,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pkg, setPkg] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!open || (!consignmentNo && !fetchUrl)) {
      return undefined;
    }

    let cancelled = false;
    const fetchDetail = async () => {
      setLoading(true);
      setError('');
      setPkg(null);
      setTracking(null);

      try {
        const url =
          fetchUrl ||
          `/api/v1/packages/by_consignment/${encodeURIComponent(String(consignmentNo).trim())}`;
        const response = await api.get(url);
        if (cancelled) return;

        if (response.data?.success) {
          setPkg(response.data.package || null);
          setTracking(response.data.tracking || null);
        } else {
          setError(response.data?.message || 'Failed to load package.');
        }
      } catch (err) {
        if (cancelled || isSessionExpiredError(err)) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load package.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [open, consignmentNo, fetchUrl, reloadKey]);

  const editable = useMemo(() => isPackageEditable(tracking, pkg), [tracking, pkg]);
  const showPayment = useMemo(() => canViewPaymentDetails(pkg), [pkg]);
  const canAct = canManagePackageActions();
  const currentStatus = tracking?.currentStatus || (pkg?.deliveredAt ? 'DELIVERED' : 'BOOKED');
  const tone = statusTone(currentStatus, theme);
  const scans = Array.isArray(tracking?.scans) ? tracking.scans : [];
  const subPackages = Array.isArray(pkg?.subPackages) ? pkg.subPackages : [];

  const timelineItems = useMemo(() => {
    const items = [];

    if (tracking?.bookedOn || tracking?.bookedBy || tracking?.bookedStatus) {
      items.push({
        key: 'booked',
        status: tracking.bookedStatus || 'BOOKED',
        at: tracking.bookedOn,
        by: tracking.bookedBy,
        subPackageLabel: null,
        scanCode: null,
      });
    }

    scans.forEach((scan, index) => {
      items.push({
        key: `scan-${scan.status}-${scan.scannedOn}-${scan.scanCode || scan.subPackageLabel || index}`,
        status: scan.status,
        at: scan.scannedOn,
        by: scan.scannedBy,
        subPackageLabel: scan.subPackageLabel,
        scanCode: scan.scanCode,
      });
    });

    return items;
  }, [tracking, scans]);

  const handleEdit = () => {
    if (!pkg || !canManagePackageActions()) return;
    if (!editable) {
      toast('This package is in transit and cannot be edited.');
      return;
    }
    onClose();
    navigate('/add-package', { state: { editData: pkg } });
  };

  const handleViewInList = () => {
    if (!canManagePackageActions()) return;
    const no = pkg?.consignmentNo || consignmentNo;
    onClose();
    navigate('/packages', {
      state: {
        headerSearch: {
          consignmentNo: no || '',
        },
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={Transition}
      transitionDuration={{ enter: 280, exit: 200 }}
      BackdropProps={{
        sx: {
          bgcolor: alpha(theme.palette.grey[900], 0.45),
          backdropFilter: 'blur(6px)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 2.5, sm: 3 },
          overflow: 'hidden',
          m: { xs: 1.5, sm: 3 },
          maxHeight: { xs: '92vh', sm: '90vh' },
          backgroundImage: `linear-gradient(
            165deg,
            ${alpha(theme.palette.primary.main, 0.07)} 0%,
            ${theme.palette.background.paper} 28%,
            ${theme.palette.background.paper} 100%
          )`,
          boxShadow: `0 28px 80px ${alpha(theme.palette.grey[900], 0.28)}`,
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: 2.25, pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.1 }}>
              Consignment
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.2,
                lineHeight: 1.2,
                wordBreak: 'break-all',
                fontSize: { xs: '1.35rem', sm: '1.75rem' },
              }}
            >
              {pkg?.consignmentNo || consignmentNo || '—'}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.45,
                  borderRadius: 10,
                  bgcolor: tone.bg,
                  color: tone.color,
                }}
              >
                <Iconify icon={tone.icon} width={16} />
                <Typography variant="caption" sx={{ fontWeight: 800 }}>
                  {formatStatusLabel(currentStatus)}
                </Typography>
              </Box>

              {tracking?.bookedOn ? (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Booked {formatDateTime(tracking.bookedOn)}
                  {tracking.bookedBy ? ` · by ${tracking.bookedBy}` : ''}
                </Typography>
              ) : null}

              {pkg?.adminName || pkg?.adminId ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={pkg.adminName || `Hub #${pkg.adminId}`}
                  sx={{ height: 24, fontWeight: 600 }}
                />
              ) : null}

              {pkg?.softDelete ? (
                <Chip size="small" color="warning" label="Deleted" sx={{ height: 24, fontWeight: 700 }} />
              ) : null}

              {!editable && !loading && pkg ? (
                <Chip
                  size="small"
                  label="Editing locked"
                  icon={<Iconify icon="solar:lock-keyhole-bold" width={14} />}
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    color: 'warning.dark',
                    '& .MuiChip-icon': { color: 'warning.dark' },
                  }}
                />
              ) : null}
            </Stack>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.08),
              '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.16) },
            }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider sx={{ borderStyle: 'dashed' }} />

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
        {loading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2.5 }} />
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2.5 }} />
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: 2.5 }} />
          </Stack>
        ) : null}

        {!loading && error ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 6, px: 2 }}>
            <Iconify icon="solar:danger-triangle-bold-duotone" width={42} sx={{ color: 'warning.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Couldn’t load package
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {error}
            </Typography>
            <Button variant="outlined" onClick={() => setReloadKey((key) => key + 1)}>
              Try again
            </Button>
          </Stack>
        ) : null}

        {!loading && !error && pkg ? (
          <Stack spacing={2.25}>
            <Box
              sx={{
                display: 'grid',
                gap: 1.75,
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              }}
            >
              <SectionCard title="Sender" icon="solar:user-bold-duotone">
                <Stack spacing={1.5}>
                  <MetaItem label="Name" value={pkg.senderName || pkg.clientName} />
                  <MetaItem label="Address" value={pkg.senderAddress} />
                  <MetaItem
                    label="City / District"
                    value={[pkg.senderDistrict, pkg.senderState, pkg.senderCountry].filter(Boolean).join(', ')}
                  />
                  <MetaItem label="Pincode" value={pkg.senderPinCode} />
                  <MetaItem label="Phone" value={pkg.senderContactNo} />
                  <MetaItem label="WhatsApp" value={pkg.senderWhatsappNo} />
                  <MetaItem label="Email" value={pkg.senderEmail} />
                  <MetaItem label="GST" value={pkg.gstNo} />
                </Stack>
              </SectionCard>

              <SectionCard title="Consignee" icon="solar:map-point-bold-duotone">
                <Stack spacing={1.5}>
                  <MetaItem label="Name" value={pkg.consigneeName || pkg.receiverName} />
                  <MetaItem label="Address" value={pkg.consigneeAddress} />
                  <MetaItem
                    label="City / District"
                    value={[pkg.consigneeDistrict, pkg.consigneeState, pkg.consigneeCountry]
                      .filter(Boolean)
                      .join(', ')}
                  />
                  <MetaItem label="Pincode" value={pkg.consigneePinCode} />
                  <MetaItem label="Phone" value={pkg.consigneeContactNo} />
                  <MetaItem label="WhatsApp" value={pkg.consigneeWhatsappNo} />
                  <MetaItem label="Email" value={pkg.consigneeEmail} />
                </Stack>
              </SectionCard>
            </Box>

            <SectionCard
              title={showPayment ? 'Package & payment' : 'Package details'}
              icon="solar:box-bold-duotone"
            >
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.75,
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                }}
              >
                <MetaItem label="Type" value={pkg.packageType} />
                <MetaItem label="Weight" value={pkg.weight != null && pkg.weight !== '' ? `${pkg.weight}` : null} />
                {showPayment ? (
                  <>
                    <MetaItem label="Amount" value={`₹${Number(pkg.amount || 0).toLocaleString('en-IN')}`} />
                    <MetaItem label="Discount" value={`₹${Number(pkg.discount || 0).toLocaleString('en-IN')}`} />
                    <MetaItem label="Total" value={`₹${Number(pkg.totalAmount || 0).toLocaleString('en-IN')}`} />
                    <MetaItem label="Payment" value={formatPaymentType(pkg.paymentType)} />
                    <MetaItem label="To pay" value={pkg.toPay ? 'Yes' : 'No'} />
                    <MetaItem label="GST total" value={`₹${Number(pkg.totalGST || 0).toLocaleString('en-IN')}`} />
                  </>
                ) : null}
                <MetaItem label="Employee" value={pkg.employeeName} />
                <MetaItem label="Delivered on" value={pkg.deliveredAt ? formatDateTime(pkg.deliveredAt) : '—'} />
                <MetaItem
                  label="Origin hub"
                  value={
                    pkg.originHub?.city || pkg.originHub?.name
                      ? [pkg.originHub?.name, pkg.originHub?.city].filter(Boolean).join(' · ')
                      : pkg.originHubId || '—'
                  }
                />
                <MetaItem
                  label="Destination hub"
                  value={
                    pkg.destinationHub?.city || pkg.destinationHub?.name
                      ? [pkg.destinationHub?.name, pkg.destinationHub?.city].filter(Boolean).join(' · ')
                      : pkg.destinationHubId || '—'
                  }
                />
                <MetaItem
                  label="Current hub"
                  value={
                    pkg.currentHub?.city || pkg.currentHub?.name
                      ? [pkg.currentHub?.name, pkg.currentHub?.city].filter(Boolean).join(' · ')
                      : pkg.currentHubId || '—'
                  }
                />
                <MetaItem
                  label="Next hub"
                  value={
                    pkg.nextHub?.city || pkg.nextHub?.name
                      ? [pkg.nextHub?.name, pkg.nextHub?.city].filter(Boolean).join(' · ')
                      : pkg.nextHubId || '—'
                  }
                />
              </Box>

              {subPackages.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    Sub packages ({subPackages.length})
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: 1 }}>
                    {subPackages.map((item, index) => (
                      <Chip
                        key={item.label || item.id || index}
                        size="small"
                        label={item.label || item.scanCode || `Pkg ${index + 1}`}
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </SectionCard>

            <SectionCard title="Tracking timeline" icon="solar:routing-2-bold-duotone">
              {timelineItems.length === 0 ? (
                <Stack alignItems="center" spacing={1} sx={{ py: 2.5 }}>
                  <Iconify icon="solar:clock-circle-bold-duotone" width={32} sx={{ color: 'text.disabled' }} />
                  <Typography variant="body2" color="text.secondary">
                    No scan history yet — package is booked.
                  </Typography>
                </Stack>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  {timelineItems.length > 1 ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 15,
                        top: 16,
                        bottom: 16,
                        width: 2,
                        bgcolor: alpha(theme.palette.grey[500], 0.22),
                        borderRadius: 1,
                        pointerEvents: 'none',
                      }}
                    />
                  ) : null}

                  {timelineItems.map((item, index) => {
                    const itemTone = statusTone(item.status, theme);
                    return (
                      <Box
                        key={item.key}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '32px 1fr',
                          columnGap: 1.75,
                          alignItems: 'flex-start',
                          pb: index === timelineItems.length - 1 ? 0 : 2.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: itemTone.bg,
                            color: itemTone.color,
                            border: `2px solid ${theme.palette.background.paper}`,
                            boxShadow: `0 0 0 1px ${alpha(itemTone.color, 0.2)}`,
                            position: 'relative',
                            zIndex: 1,
                          }}
                        >
                          <Iconify icon={itemTone.icon} width={15} />
                        </Box>

                        <Box sx={{ minWidth: 0, pt: 0.35 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {formatStatusLabel(item.status)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {formatDateTime(item.at)}
                          </Typography>
                          {item.by ? (
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.6 }}>
                              <Iconify
                                icon="solar:user-bold-duotone"
                                width={14}
                                sx={{ color: 'text.disabled', flexShrink: 0 }}
                              />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {item.by}
                              </Typography>
                            </Stack>
                          ) : null}
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                            {item.subPackageLabel ? (
                              <Chip size="small" variant="outlined" label={item.subPackageLabel} sx={{ height: 22 }} />
                            ) : null}
                            {item.scanCode ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={item.scanCode}
                                sx={{ height: 22, maxWidth: 220 }}
                              />
                            ) : null}
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </SectionCard>
          </Stack>
        ) : null}
      </DialogContent>

      <Divider sx={{ borderStyle: 'dashed' }} />

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          gap: 1,
          flexWrap: 'wrap',
          bgcolor: alpha(theme.palette.background.paper, 0.9),
        }}
      >
        <Button color="inherit" onClick={onClose} sx={{ mr: 'auto' }}>
          Close
        </Button>

        {canAct && showViewInList ? (
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleViewInList}
            disabled={loading || !pkg}
            startIcon={<Iconify icon="eva:list-outline" />}
            sx={{ borderColor: alpha(theme.palette.grey[500], 0.32) }}
          >
            View in list
          </Button>
        ) : null}

        {canAct ? (
          <Tooltip
            title={!editable && pkg ? 'Package is moving — editing is locked' : ''}
            disableHoverListener={editable || !pkg}
          >
            <span>
              <Button
                variant="contained"
                onClick={handleEdit}
                disabled={loading || !pkg || !editable}
                aria-label="Edit"
              >
                <Iconify icon={editable ? 'eva:edit-fill' : 'solar:lock-keyhole-bold'} />
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

PackageDetailModal.propTypes = {
  open: PropTypes.bool,
  consignmentNo: PropTypes.string,
  onClose: PropTypes.func,
  showViewInList: PropTypes.bool,
  fetchUrl: PropTypes.string,
};

export { isPackageEditable };
